import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Navigation, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import { getExperienceCategory } from '../../utils/experienceCategories';
import { ExperienceCategoryGlyph } from '../host/ExperienceCategoryGlyph';
import { reactToExperience } from '../../utils/experiencesApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Open the phone's native maps app with directions to the pin. Experiences
 * don't have an address / Google place id like venues do, so we route to
 * raw coordinates on both platforms — this matches how the "Refer an
 * Experience" flow already treats the location.
 */
function openExperienceDirections(lat, lng) {
  if (typeof lat !== 'number' || typeof lng !== 'number') return;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const dest = `${lat},${lng}`;
  if (isIOS) {
    window.open(`maps://maps.apple.com/?daddr=${dest}`, '_blank');
    return;
  }
  window.open(`https://www.google.com/maps/dir/?api=1&destination=${dest}`, '_blank');
}

/**
 * Compact "net" number a guest sees on the like button — likes minus
 * dislikes, clamped so the UI never shows a negative count.
 */
function computeNet(like, dislike) {
  return Math.max((like || 0) - (dislike || 0), 0);
}

/**
 * Bottom-sheet card that opens when a guest taps an experience pin.
 *
 * NOTE (z-index): the sheet is `position: fixed` with a z-index above the
 * guest bottom nav (`z-[9999]` on GuestBottomNav) so it always sits ON TOP
 * of the nav. The map section itself is `zIndex: 50`, so any child
 * rendered `position: absolute` inside it would be clipped behind the
 * nav pill — the fixed position + high z-index is the fix.
 */
export function ExperiencePreviewSheet({ experience, onClose, onReactionChange }) {
  const initialMy = experience?.my_reaction || null;
  const initialLike = experience?.like_count || 0;
  const initialDislike = experience?.dislike_count || 0;

  const [myReaction, setMyReaction] = useState(initialMy);
  const [likeCount, setLikeCount] = useState(initialLike);
  const [dislikeCount, setDislikeCount] = useState(initialDislike);
  const [busy, setBusy] = useState(false);

  // When the caller swaps to a different pin, reset local state so the
  // previous card's counts / selection don't briefly flash on the new one.
  useEffect(() => {
    setMyReaction(experience?.my_reaction || null);
    setLikeCount(experience?.like_count || 0);
    setDislikeCount(experience?.dislike_count || 0);
  }, [experience?.id, experience?.my_reaction, experience?.like_count, experience?.dislike_count]);

  if (!experience) return null;
  const cat = getExperienceCategory(experience.category);
  const photoSrc = experience.photo_url
    ? `${BACKEND_URL}/api/experiences/photo/${experience.photo_url}`
    : null;
  const tags = Array.isArray(experience.tags) ? experience.tags.slice(0, 3) : [];
  const net = computeNet(likeCount, dislikeCount);

  const handleReact = async (value) => {
    // Toggle off when tapping the same button twice.
    const nextValue = myReaction === value ? null : value;
    const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;
    if (!token) {
      toast('Sign in to like or flag experiences.', { id: 'experience-reaction-signin', duration: 3000 });
      return;
    }
    if (busy) return;
    // Optimistic update first.
    const prev = { myReaction, likeCount, dislikeCount };
    let nextLike = likeCount;
    let nextDislike = dislikeCount;
    if (myReaction === 'like') nextLike -= 1;
    if (myReaction === 'dislike') nextDislike -= 1;
    if (nextValue === 'like') nextLike += 1;
    if (nextValue === 'dislike') nextDislike += 1;
    setMyReaction(nextValue);
    setLikeCount(Math.max(nextLike, 0));
    setDislikeCount(Math.max(nextDislike, 0));
    setBusy(true);
    try {
      const data = await reactToExperience(experience.id, nextValue);
      setLikeCount(data.like_count || 0);
      setDislikeCount(data.dislike_count || 0);
      setMyReaction(data.my_reaction || null);
      if (typeof onReactionChange === 'function') {
        onReactionChange(experience.id, {
          like_count: data.like_count || 0,
          dislike_count: data.dislike_count || 0,
          my_reaction: data.my_reaction || null,
        });
      }
    } catch (err) {
      // Roll back optimistic state on failure.
      setMyReaction(prev.myReaction);
      setLikeCount(prev.likeCount);
      setDislikeCount(prev.dislikeCount);
      toast.error(err.response?.data?.detail || 'Could not save your reaction');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="guest-experience-preview-sheet"
      style={{
        position: 'fixed',
        left: '50%',
        // Anchor above the compact bottom-nav pill: nav height ~58px +
        // 8px gap + safe-area inset. Matches the visual spacing of the
        // venue preview sheet so the two cards feel like siblings.
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 78px)',
        transform: 'translateX(-50%)',
        width: 'calc(100% - 24px)',
        maxWidth: 366,
        // ABOVE the guest bottom-nav pill (z-[9999]) so the card never
        // sits behind the nav. Fixed positioning also escapes the map
        // section's `overflow: hidden` clip.
        zIndex: 10500,
        background: 'var(--surface)',
        borderRadius: 22,
        boxShadow: '0 24px 60px rgba(15,17,21,0.28)',
        overflow: 'hidden',
        maxHeight: 'min(72vh, 500px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ position: 'relative', width: '100%', height: 160, background: '#EAF0FF', flexShrink: 0 }}>
        {photoSrc ? (
          <img
            src={photoSrc}
            alt={cat?.display || 'Experience'}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div style={{
            width: '100%', height: '100%',
            background: `linear-gradient(135deg, ${cat?.color || '#7C3AED'}33, ${cat?.color || '#7C3AED'}11)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              width: 60, height: 60, borderRadius: '50%',
              background: cat?.color || '#7C3AED',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ExperienceCategoryGlyph glyph={cat?.glyph || 'target'} size={28} />
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={onClose}
          data-testid="guest-experience-preview-close"
          aria-label="Close"
          style={{
            position: 'absolute', top: 10, right: 10,
            width: 32, height: 32, borderRadius: '50%',
            background: 'rgba(15,17,21,0.55)',
            border: 'none', color: '#FFFFFF',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <X size={16} />
        </button>
        {/* Category chip pinned to the bottom-left of the photo */}
        <div style={{
          position: 'absolute', left: 12, bottom: 10,
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '5px 10px', borderRadius: 999,
          background: cat?.color || '#7C3AED', color: '#FFFFFF',
          fontFamily: "'Inria Serif', serif", fontSize: 12, fontWeight: 700,
          boxShadow: '0 6px 14px rgba(8,12,24,0.30)',
        }}>
          <ExperienceCategoryGlyph glyph={cat?.glyph || 'target'} size={12} />
          {cat?.display || 'Experience'}
        </div>
      </div>

      {tags.length > 0 ? (
        <div style={{ padding: '12px 14px 0' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {tags.map((tag) => (
              <span
                key={tag}
                data-testid={`guest-experience-preview-tag-${tag.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                style={{
                  padding: '4px 10px', borderRadius: 999,
                  background: 'var(--surface-2, #F4F1FC)',
                  color: 'var(--text)',
                  fontFamily: "'Inria Serif', serif", fontSize: 12, fontWeight: 600,
                  border: '1px solid var(--card-border)',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <div
        data-testid="guest-experience-preview-description"
        style={{
          padding: '10px 14px 12px',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          fontFamily: "'Inria Serif', serif",
          fontSize: 14,
          lineHeight: 1.45,
          color: 'var(--text)',
          flex: 1,
        }}
      >
        {experience.description || 'A hidden gem shared by a local host.'}
      </div>

      {/* Action row — likes / dislikes / get directions. Sits above the
          scrollable description so it's always reachable without having
          to scroll to the bottom of a long story. */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 12px calc(12px + env(safe-area-inset-bottom, 0px) * 0.15)',
        borderTop: '1px solid var(--card-border)',
        background: 'var(--surface)',
        flexShrink: 0,
      }}>
        <ReactionButton
          testId={`guest-experience-preview-like-${experience.id}`}
          active={myReaction === 'like'}
          activeColor="#16A34A"
          onClick={() => handleReact('like')}
          disabled={busy}
          icon={<ThumbsUp size={16} strokeWidth={2.2} />}
          count={net}
          label={`${net} likes`}
        />
        <ReactionButton
          testId={`guest-experience-preview-dislike-${experience.id}`}
          active={myReaction === 'dislike'}
          activeColor="#DC2626"
          onClick={() => handleReact('dislike')}
          disabled={busy}
          icon={<ThumbsDown size={16} strokeWidth={2.2} />}
          count={dislikeCount}
          label={`${dislikeCount} dislikes`}
          hideZero
        />
        <button
          type="button"
          onClick={() => openExperienceDirections(experience.latitude, experience.longitude)}
          data-testid={`guest-experience-preview-directions-${experience.id}`}
          style={{
            marginLeft: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '9px 14px',
            borderRadius: 999,
            border: 'none',
            background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
            color: '#FFFFFF',
            fontFamily: "'Inria Serif', serif",
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 8px 18px rgba(124, 58, 237, 0.32)',
            whiteSpace: 'nowrap',
          }}
        >
          <Navigation size={14} strokeWidth={2.4} />
          Get directions
        </button>
      </div>
    </div>
  );
}

function ReactionButton({ testId, active, activeColor, onClick, disabled, icon, count, label, hideZero }) {
  const showCount = !hideZero || count > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      aria-label={label}
      aria-pressed={active}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        padding: '8px 12px',
        minWidth: 62,
        borderRadius: 999,
        border: `1.5px solid ${active ? activeColor : 'var(--card-border)'}`,
        background: active ? `${activeColor}18` : 'var(--surface)',
        color: active ? activeColor : 'var(--text)',
        fontFamily: "'Inria Serif', serif",
        fontSize: 13,
        fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'background 140ms ease, color 140ms ease, border-color 140ms ease',
      }}
    >
      {icon}
      {showCount ? <span>{count}</span> : null}
    </button>
  );
}

export default ExperiencePreviewSheet;
